import { Navigate } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useOrganization } from '../../../shared/features/organization/components/OrganizationProvider';
import { TenantLogo } from '../../../shared/features/organization/components/TenantLogo';
import { ColorModeToggle } from '../../../shared/features/theme/components/ColorModeToggle';
import { LoginBrandPanel } from '../components/LoginBrandPanel';
import { LoginCodeForm } from '../components/LoginCodeForm';
import { LoginCodeOption } from '../components/LoginCodeOption';
import { LoginPasswordForm } from '../components/LoginPasswordForm';
import { LoginSignInCodeForm } from '../components/LoginSignInCodeForm';
import { LoginStartForm } from '../components/LoginStartForm';
import { LoginStartSkeleton } from '../components/LoginStartSkeleton';
import { useLoginPage, type LoginPageState } from '../hooks/use-login-page';
import { passwordStepLead } from '../policies/login-step.policy';

function CurrentStep({ page, title }: { page: LoginPageState; title: string }) {
  if (page.isLoading) return <LoginStartSkeleton title={title} />;
  const codeOption = page.isCodeOffered && <LoginCodeOption onClick={page.showStart} />;

  switch (page.step) {
    case 'start':
      return <LoginStartForm title={title} step={page.codeStep} onUsePassword={page.showPassword} />;
    case 'code':
      return <LoginSignInCodeForm step={page.codeStep} onUseDifferentIdentifier={page.showStart} onUsePassword={page.showPassword} />;
    case 'twoFactor':
      return (
        <>
          <LoginCodeForm step={page.twoFactorStep} />
          {codeOption}
        </>
      );
    case 'password':
      return (
        <>
          <LoginPasswordForm
            title={title}
            lead={passwordStepLead(page.isCodeOffered)}
            initialEmail={page.passwordEmail}
            error={page.error}
            submitting={page.submitting}
            onSubmit={page.submitPassword}
          />
          {codeOption}
        </>
      );
  }
}

export function LoginPage() {
  const { state } = useCurrentUser();
  const organization = useOrganization();
  const page = useLoginPage();

  // An already-authenticated visit (back button, bookmarked /login) should land on the app, not the form.
  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  return (
    <div className="grid min-h-screen bg-surface lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <LoginBrandPanel />
      <main className="relative flex items-center justify-center px-6 py-12">
        <div className="absolute top-5 right-5">
          <ColorModeToggle />
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-10 lg:hidden">
            <TenantLogo size="login" />
          </div>
          <CurrentStep page={page} title={organization.configured ? `Sign in to ${organization.name}` : 'Sign in'} />
        </div>
      </main>
    </div>
  );
}
